import { useMemo, useState } from "react";
import Icon from "../icons/Icon";
import i18n from "../../language/i18n";

// A point-in-time marker. `start` = item begins (hollow-ring dot), `due` = item
// deadline (filled dot), `posted` = a single-date event such as an announcement
// (filled dot). The marker drives both the day-cell dot shape and the detail-row
// chip label (cal_starts / cal_due / cal_posted).
export type CalendarEventMarker = "start" | "due" | "posted";

export interface CalendarEvent {
  // Stable identity for React keys / de-dup (e.g. `assignment-12-due`).
  id: string | number;
  ts: number; // epoch millis — when the marker falls
  marker: CalendarEventMarker;
  title: string;
  kindLabel?: string; // small label under the title, e.g. "Destek Ödevi" / "Duyuru"
  iconName?: string; // library Icon name for the detail-row tile
  ink: string; // accent color (dot + tile foreground)
  tint?: string; // tile background
  route?: string; // optional deep-link target (handled by onEventClick)
}

const MARKER_CHIP_KEY: Record<CalendarEventMarker, string> = {
  due: "cal_due",
  start: "cal_starts",
  posted: "cal_posted",
};
// Within a day: due first, then posted, then starts; ties broken by time.
const MARKER_ORDER: Record<CalendarEventMarker, number> = {
  due: 0,
  posted: 1,
  start: 2,
};
// A busy day (e.g. dozens of evaluation announcements) collapses to this many
// rows by default; the rest expand behind a "+N more" toggle and the whole list
// scrolls within a fixed cap so the calendar's footprint never blows up.
const COLLAPSED_EVENT_COUNT = 6;

interface CalendarProps {
  // Full event list across any months — the calendar filters to the shown month
  // internally, so month navigation works without the caller re-querying.
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  showLegend?: boolean;
  emptyLabel?: string;
  dataTour?: string;
}

const Calendar = ({
  events,
  onEventClick,
  showLegend = true,
  emptyLabel,
  dataTour,
}: CalendarProps) => {
  const now = new Date();
  const todayY = now.getFullYear();
  const todayM = now.getMonth();
  const today = now.getDate();
  const [view, setView] = useState({ y: todayY, m: todayM });
  const isCurrentMonth = view.y === todayY && view.m === todayM;
  const [selDay, setSelDay] = useState(today);
  // Collapsed by default; reset whenever the selected day changes (selectDay).
  const [expanded, setExpanded] = useState(false);

  // Day → events map for the *shown* month (so month navigation works and an
  // item whose range crosses a month boundary still shows the in-month marker).
  const byDay = useMemo(() => {
    const map: Record<number, CalendarEvent[]> = {};
    events.forEach((e) => {
      if (!e.ts) return;
      const d = new Date(e.ts);
      if (d.getFullYear() !== view.y || d.getMonth() !== view.m) return;
      const day = d.getDate();
      (map[day] ||= []).push(e);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) =>
        a.marker === b.marker
          ? a.ts - b.ts
          : MARKER_ORDER[a.marker] - MARKER_ORDER[b.marker],
      ),
    );
    return map;
  }, [events, view.y, view.m]);

  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const lead = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const monthLabel = new Date(view.y, view.m, 1).toLocaleDateString(
    i18n.language,
    { month: "long", year: "numeric" },
  );
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(i18n.language, {
      weekday: "short",
    }),
  );
  const dayEvents = byDay[selDay] || [];
  const fmtTime = (ts: number) =>
    new Date(ts).toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
    });

  const selectDay = (day: number) => {
    setSelDay(day);
    setExpanded(false);
  };
  const goMonth = (delta: number) => {
    const d = new Date(view.y, view.m + delta, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
    // keep "today" selected when returning to this month, else first of month
    selectDay(d.getFullYear() === todayY && d.getMonth() === todayM ? today : 1);
  };
  const goToday = () => {
    setView({ y: todayY, m: todayM });
    selectDay(today);
  };

  return (
    <div className="cal" data-tour={dataTour}>
      <div className="cal-head">
        <div className="cal-title">
          <Icon name="calendar" size={20} /> {monthLabel}
        </div>
        <div className="cal-nav">
          {!isCurrentMonth ? (
            <button className="cal-today" onClick={goToday}>
              {i18n.t("today")}
            </button>
          ) : null}
          <button
            className="cal-navbtn"
            onClick={() => goMonth(-1)}
            aria-label={i18n.t("previous")}
          >
            <Icon name="chevronLeft" size={16} />
          </button>
          <button
            className="cal-navbtn"
            onClick={() => goMonth(1)}
            aria-label={i18n.t("next")}
          >
            <Icon name="chevronRight" size={16} />
          </button>
        </div>
      </div>
      <div className="cal-grid cal-grid--head">
        {weekdays.map((w, i) => (
          <span className="cal-wd" key={i}>
            {w}
          </span>
        ))}
      </div>
      <div className="cal-grid">
        {Array.from({ length: lead }).map((_, i) => (
          <span className="cal-cell is-blank" key={"b" + i} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const evs = byDay[day] || [];
          const isToday = isCurrentMonth && day === today;
          return (
            <button
              key={day}
              className={
                "cal-cell" +
                (isToday ? " is-today" : "") +
                (day === selDay ? " is-sel" : "") +
                (evs.length ? " is-ev" : "")
              }
              onClick={() => selectDay(day)}
            >
              <span>{day}</span>
              {evs.length > 3 ? (
                // Busy day: a single count pip (with a dominant-ink dot) reads as
                // "N things" far better than 3 dots + a faint "+49".
                <span className="cal-count">
                  <span
                    className="cal-count-dot"
                    style={{
                      background:
                        evs[0].marker === "start" ? "transparent" : evs[0].ink,
                      borderColor: evs[0].ink,
                    }}
                  />
                  {evs.length}
                </span>
              ) : evs.length ? (
                <span className="cal-dots">
                  {evs.map((e, k) => (
                    <span
                      className={"cal-dot cal-dot--" + e.marker}
                      key={k}
                      style={{
                        background: e.marker === "start" ? "transparent" : e.ink,
                        borderColor: e.ink,
                      }}
                    />
                  ))}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {showLegend ? (
        <div className="cal-legend">
          <span className="cal-leg">
            <span className="cal-dot cal-dot--due" /> {i18n.t("cal_due")}
          </span>
          <span className="cal-leg">
            <span className="cal-dot cal-dot--start" /> {i18n.t("cal_starts")}
          </span>
        </div>
      ) : null}
      <div className="cal-detail">
        <div className="cal-detail-head">
          <span className="cal-detail-date">
            {selDay} {monthLabel}
          </span>
          {dayEvents.length ? (
            <span className="cal-detail-count">{dayEvents.length}</span>
          ) : null}
        </div>
        {dayEvents.length ? (
          <>
            <div className="cal-detail-list">
              {(expanded
                ? dayEvents
                : dayEvents.slice(0, COLLAPSED_EVENT_COUNT)
              ).map((e) => (
                <button
                  className="cal-event"
                  key={e.id + "-" + e.marker}
                  onClick={() => onEventClick?.(e)}
                >
                  <span
                    className="cal-event-ic"
                    style={{ background: e.tint, color: e.ink }}
                  >
                    {e.iconName ? (
                      <Icon name={e.iconName as any} size={15} />
                    ) : null}
                  </span>
                  <span className="cal-event-main">
                    <span className="cal-event-title">{e.title}</span>
                    {e.kindLabel ? (
                      <span className="cal-event-kind">{e.kindLabel}</span>
                    ) : null}
                  </span>
                  <span className="cal-event-right">
                    <span className={"cal-chip cal-chip--" + e.marker}>
                      {i18n.t(MARKER_CHIP_KEY[e.marker])}
                    </span>
                    <span className="cal-event-time">{fmtTime(e.ts)}</span>
                  </span>
                </button>
              ))}
            </div>
            {dayEvents.length > COLLAPSED_EVENT_COUNT ? (
              <button
                className="cal-detail-toggle"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded
                  ? i18n.t("show_less")
                  : "+" +
                    (dayEvents.length - COLLAPSED_EVENT_COUNT) +
                    " " +
                    i18n.t("more")}
              </button>
            ) : null}
          </>
        ) : (
          <div className="cal-empty">{emptyLabel ?? i18n.t("cal_no_events")}</div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
